<template>
  <div 
    class="delivery-card"
    :class="{'has-pending-updates': hasPendingUpdates}"
    @click="$emit('click', delivery)">
    <div class="card-content">
      <div class="card-header" :class="getStatusBackgroundClass(delivery.status)">
        <div class="position-badge">{{ delivery.route_position }}</div>
        <h4 class="delivery-title">
          {{ getDeliveryTitle(delivery) }}
        </h4>
        <div v-if="hasPendingUpdates" class="pending-indicator">
          <i class="pi pi-clock"></i>
        </div>
      </div>
      
      <div class="card-body">
        <div class="address-section">
          <i class="pi pi-map-marker location-icon"></i>
          <span class="address-text">{{ formatAddress(delivery.deliveryLocation.address) }}</span>
        </div>

        <div class="packages-section">
          <div v-for="packageDelivery in delivery.package_deliveries" :key="packageDelivery.id"
            class="package-item">
            <span class="quantity">{{ packageDelivery.quantity }}×</span>
            <span class="name">{{ packageDelivery.package.name }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

// Props
const props = defineProps({
  delivery: {
    type: Object,
    required: true
  },
  hasPendingUpdates: {
    type: Boolean,
    default: false
  }
});

// Emit events
const emit = defineEmits(['click']);

// Méthodes
const getDeliveryTitle = (delivery) => {
  if (!delivery.deliveryLocation || !delivery.organisation) return '';
  
  return areNamesIdentical(delivery.deliveryLocation.name, delivery.organisation.name)
    ? delivery.deliveryLocation.name
    : `${delivery.deliveryLocation.name} - ${delivery.organisation.name}`;
};

const areNamesIdentical = (name1, name2) => {
  if (!name1 || !name2) return false;
  return name1.toLowerCase() === name2.toLowerCase();
};

const formatAddress = (address) => {
  if (!address) return '';
  // Supprimer ', France' à la fin si présent
  let formattedAddress = address.replace(/, France$/, '');
  // Supprimer le code postal et la ville s'ils sont répétés dans l'adresse
  const regex = /, \d{5} [^,]+$/;
  if (regex.test(formattedAddress)) {
    formattedAddress = formattedAddress.replace(regex, '');
  }
  return formattedAddress;
};

const getStatusBackgroundClass = (status) => {
  const backgroundMap = {
    'PLANNED': 'bg-blue',
    'DELIVERED': 'bg-green',
    'NOT DELIVERED': 'bg-red',
    'DO NOT DELIVER': 'bg-purple',
    'IN PROGRESS': 'bg-orange'
  };
  return backgroundMap[status] || 'bg-gray';
};

const getStatusColorClass = (status) => {
  const colorMap = {
    'PLANNED': 'status-blue',
    'DELIVERED': 'status-green',
    'NOT DELIVERED': 'status-red',
    'DO NOT DELIVER': 'status-purple',
    'IN PROGRESS': 'status-orange'
  };
  return colorMap[status] || 'status-gray';
};

const getStatusLabel = (status) => {
  const labelMap = {
    'PLANNED': 'Planifiée',
    'DELIVERED': 'Livrée',
    'NOT DELIVERED': 'Non livrée',
    'DO NOT DELIVER': 'Ne pas livrer',
    'IN PROGRESS': 'En cours'
  };
  return labelMap[status] || status;
};
</script>

<style scoped>
.delivery-card {
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  position: relative;
  border: 1px solid #f1f5f9;
}

.delivery-card:active {
  transform: translateY(1px);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.delivery-card.has-pending-updates {
  border-left: 3px solid #fbbf24;
}

.card-content {
  position: relative;
  z-index: 1;
}

.card-header {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  position: relative;
  gap: 12px;
}

.bg-blue {
  background: linear-gradient(to right, #3b82f6, #60a5fa);
}

.bg-green {
  background: linear-gradient(to right, #10b981, #34d399);
}

.bg-red {
  background: linear-gradient(to right, #ef4444, #f87171);
}

.bg-purple {
  background: linear-gradient(to right, #8b5cf6, #a78bfa);
}

.bg-orange {
  background: linear-gradient(to right, #f59e0b, #fbbf24);
}

.bg-gray {
  background: linear-gradient(to right, #6b7280, #9ca3af);
}

.position-badge {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.25);
  color: #ffffff;
  font-weight: 600;
  flex-shrink: 0;
  border: 2px solid rgba(255, 255, 255, 0.5);
  font-size: 14px;
}

.delivery-title {
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.1);
}

.pending-indicator {
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.card-body {
  padding: 16px;
  background-color: #ffffff;
}

.address-section {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 16px;
  color: #4b5563;
}

.location-icon {
  color: #3b82f6;
  font-size: 16px;
  margin-top: 3px;
  flex-shrink: 0;
}

.address-text {
  font-size: 15px;
  line-height: 1.4;
}

.packages-section {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.package-item {
  display: inline-flex;
  align-items: center;
  background-color: #f3f4f6;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #4b5563;
}

.package-item .quantity {
  font-weight: 600;
  margin-right: 4px;
}

.status-indicator {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #6b7280;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}

.status-indicator.status-blue .status-dot { background-color: #3b82f6; }
.status-indicator.status-green .status-dot { background-color: #10b981; }
.status-indicator.status-red .status-dot { background-color: #ef4444; }
.status-indicator.status-purple .status-dot { background-color: #8b5cf6; }
.status-indicator.status-orange .status-dot { background-color: #f59e0b; }
.status-indicator.status-gray .status-dot { background-color: #6b7280; }

.status-indicator.status-blue .status-text { color: #3b82f6; }
.status-indicator.status-green .status-text { color: #10b981; }
.status-indicator.status-red .status-text { color: #ef4444; }
.status-indicator.status-purple .status-text { color: #8b5cf6; }
.status-indicator.status-orange .status-text { color: #f59e0b; }

.pending-text {
  margin-left: auto;
  color: #fbbf24;
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.pending-text::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: #fbbf24;
  margin-right: 6px;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

@media (min-width: 640px) {
  .card-header {
    padding: 16px 20px;
  }
  
  .card-body {
    padding: 16px 20px;
  }
  
  .delivery-title {
    font-size: 18px;
  }
}
</style> 